import React, { useState, useEffect, useRef, MutableRefObject, useCallback } from 'react';
import styled, { css } from 'styled-components';
import throttle from 'Common/helpers/throttle';

export enum PopupPositions {
    LEFT = 'left',
    MIDDLE = 'middle',
    RIGHT = 'right',
}

const Wrapper = styled.span`
    position: relative;
`;

const TextPopupWrapper = styled.span<{ $isVisible: boolean; $popupPosition: PopupPositions }>`
    display: ${props => (props.$isVisible ? 'block' : 'none')};
    position: absolute;
    ${({ $popupPosition }) => {
        switch ($popupPosition) {
            case PopupPositions.LEFT:
                return css`
                    left: 0;
                `;
            case PopupPositions.RIGHT:
                return css`
                    right: 0;
                `;
            default:
                return css`
                    left: 50%;
                    transform: translate(-50%);
                `;
        }
    }}
    top: 20px;
    padding: 0 10px;
    height: 30px;
    border: 1px solid ${({ theme }) => theme.colors.charcoal20New};
    border-radius: 2px;
    box-shadow: 0 1px 2px 0 ${({ theme }) => theme.colors.charcoal50};
    background-color: ${({ theme }) => theme.colors.white};
    line-height: 30px;
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.charcoal100};
    z-index: ${({ theme }) => theme.zIndexPopUpOverlay};

    &::before {
        border: 1px solid ${({ theme }) => theme.colors.charcoal20New};
        border-width: 1px 0 0 1px;
        content: '';
        height: 8px;

        ${({ $popupPosition }) => {
            switch ($popupPosition) {
                case PopupPositions.LEFT:
                    return css`
                        margin-left: 10px;
                    `;
                case PopupPositions.RIGHT:
                    return css`
                        right: 16px;
                    `;
                default:
                    return css`
                        left: 50%;
                        margin-left: -4px;
                    `;
            }
        }}
        transform: rotate(45deg);
        width: 8px;
        top: -4px;
        display: block;
        position: absolute;
        background-color: ${({ theme }) => theme.colors.white};
    }
`;

const TextPopupTrigger = styled.span`
    text-decoration: underline;
    cursor: pointer;
`;

const useHidePopup = (ref: MutableRefObject<any>, callback: () => void) => {
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target)) {
                callback();
            }
        };

        const handleResize = throttle(() => {
            callback();
        }, 300);

        window.addEventListener('resize', handleResize);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [callback, ref]);
};

export const usePopupPosition = (
    isTextPopupVisible: boolean,
    setPopupPosition: (arg: PopupPositions) => void,
    textPopupRef: React.RefObject<HTMLSpanElement>,
    containerRef?: React.RefObject<HTMLElement>
    // eslint-disable-next-line max-params
) => {
    useEffect(() => {
        if (isTextPopupVisible && textPopupRef.current && containerRef?.current) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const textPopupRect = textPopupRef.current.getBoundingClientRect();
            const isOutsideLeft = containerRect && textPopupRect && textPopupRect.left < containerRect.left;
            const isOutsideRight = containerRect && textPopupRect && textPopupRect.right > containerRect.right;

            if (isOutsideLeft) {
                setPopupPosition(PopupPositions.LEFT);
                return;
            }

            if (isOutsideRight) {
                setPopupPosition(PopupPositions.RIGHT);
            }
        }
    }, [containerRef, isTextPopupVisible, setPopupPosition, textPopupRef]);
};

interface TextPopupProps extends React.HTMLAttributes<HTMLDivElement> {
    buttonText: string;
    popupText: string;
    containerRef?: React.RefObject<HTMLElement>;
}

const TextPopup: React.FunctionComponent<TextPopupProps> = ({ buttonText, popupText, containerRef }) => {
    const [isTextPopupVisible, setIsTextPopupVisible] = useState(false);
    const [popupPosition, setPopupPosition] = useState<PopupPositions>(PopupPositions.MIDDLE);
    const textPopupTriggerRef = useRef(null);
    const textPopupRef = useRef<HTMLSpanElement>(null);

    const hidePopup = useCallback(() => {
        setIsTextPopupVisible(false);
        setPopupPosition(PopupPositions.MIDDLE);
    }, []);
    useHidePopup(textPopupTriggerRef, hidePopup);
    usePopupPosition(isTextPopupVisible, setPopupPosition, textPopupRef, containerRef);

    return (
        <Wrapper>
            <TextPopupTrigger
                tabIndex={0}
                onKeyDown={e => {
                    e.preventDefault();
                    setIsTextPopupVisible(true);
                }}
                onClick={e => {
                    // Preventing to not trigger the validation in the form
                    e.preventDefault();
                    setIsTextPopupVisible(true);
                }}
                role="button"
                ref={textPopupTriggerRef}
            >
                {buttonText}
            </TextPopupTrigger>
            <TextPopupWrapper
                $popupPosition={popupPosition}
                ref={textPopupRef}
                role="tooltip"
                $isVisible={isTextPopupVisible}
            >
                {popupText}
            </TextPopupWrapper>
        </Wrapper>
    );
};

export default TextPopup;
